import {
  DateField,
  Edit,
  List,
  Pagination,
  NumberField,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  useDataProvider,
  useRecordContext,
  useTranslate,
  TopToolbar,
  ExportButton,
  useListContext,
  useNotify
} from 'react-admin';
import { Box } from '@mui/material';
import jsonExport from 'jsonexport/dist';
import Button from '@mui/material/Button';
import { PAYMENT_STATUS } from '../common_modules/constants/choices';
import EnumTextField from '../common_modules/components/EnumTextField';
import NumericRadioButtonGroupInput from '../common_modules/components/NumericRadioButtonGroupInput';
import FlexibleListLayout from '../common_modules/components/FlexibleListLayout';
import { formatInTimeZone } from 'date-fns-tz';
import downloadCSVCompat from '../common_modules/utils/downloadCSVCompat';
import { MAX_PER_PAGE } from '../common_modules/constants/define';

const ListActionsWithExport = ({ exporter }: { exporter: (records: any[]) => void }) => {
  const { total } = useListContext();
  return (
    <TopToolbar>
      <ExportButton label="CSVダウンロード" exporter={exporter} disabled={total === 0} maxResults={MAX_PER_PAGE} />
    </TopToolbar>
  );
};

const LinkToInvoiceButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const notify = useNotify();
  if (!record) return null;
  const hasLink = record.invoiceLink !== null;

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await dataProvider.download(record.invoiceLink);
    } catch (error) {
      if (error instanceof Error) {
        notify(error.message || '請求書の表示に失敗しました', { type: 'warning' });
      } else {
        notify('請求書の表示に失敗しました', { type: 'warning' });
      }
    }
  };
  return (
    hasLink && (
      <Button
        size="small"
        color="primary"
        variant="contained"
        sx={{ display: 'inline-flex', alignItems: 'center' }}
        onClick={handleClick}
      >
        {translate('resources.invoices.fields.invoicePdf')}
      </Button>
    )
  );
};

const LinkToReceiptButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const notify = useNotify();
  if (!record) return null;
  const canShowButton = record.receiptLink !== null && record.status === 2;

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await dataProvider.download(record.receiptLink);
    } catch (error) {
      if (error instanceof Error) {
        notify(error.message || '領収書の表示に失敗しました', { type: 'warning' });
      } else {
        notify('領収書の表示に失敗しました', { type: 'warning' });
      }
    }
  };
  return (
    canShowButton && (
      <Button
        size="small"
        color="primary"
        variant="contained"
        sx={{ display: 'inline-flex', alignItems: 'center' }}
        onClick={handleClick}
      >
        {translate('resources.invoices.fields.receiptPdf')}
      </Button>
    )
  );
};

const LinkToInvoiceDetailButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  if (!record) return null;

  const hasLink = record.invoiceLink !== null;
  return (
    hasLink && (
      <Button
        size="small"
        color="primary"
        variant="contained"
        sx={{ display: 'inline-flex', alignItems: 'center' }}
        onClick={(event) => {
          event.stopPropagation();
          dataProvider.download(record.invoiceDetailLink);
        }}
      >
        {translate('resources.invoices.fields.invoiceDetailPdf')}
      </Button>
    )
  );
};

export const InvoiceList = () => {
  const translate = useTranslate();

  const handleExport = (records: any[]) => {
    if (!records || records.length === 0) {
      console.log('No records to export');
      return;
    }

    const exportFields = ['issueDate', 'paymentDeadline', 'status', 'detailPageLink'];
    const keys = exportFields;
    // ヘッダー名は翻訳キー 'resources.invoices.fields.detailPageLink' から取得されます
    const headers = keys.map((key) => translate(`resources.invoices.fields.${key}`, { _: key }));

    const dataToExport = records.map((record) => {
      const transformedRecord: Record<string, any> = {};
      keys.forEach((key) => {
        let value;

        // ★★★ 日付フィールドのフォーマット処理を追加 ★★★
        if ((key === 'issueDate' || key === 'paymentDeadline') && record[key]) {
          value = formatInTimeZone(new Date(record[key]), 'UTC', 'yyyy/MM/dd HH:mm:ss');
        } else if (key === 'detailPageLink') {
          value = `${window.location.origin}/#/invoices/${record.id}/show`;
        } else if (key === 'status') {
          value = translate(`values.depositStatus.${record[key]}`, { _: record[key] });
        } else {
          value = record[key];
        }
        transformedRecord[translate(`resources.invoices.fields.${key}`, { _: key })] = value || '';
      });
      return transformedRecord;
    });

    // CSVエクスポート処理
    jsonExport(dataToExport, { headers }, (err: Error | null, csv: string) => {
      if (err) {
        console.error(err);
        return;
      }
      const bom = '\uFEFF';
      const csvWithBom = bom + csv;
      downloadCSVCompat(csvWithBom, '請求');
    });
  };

  return (
    <List
      actions={<ListActionsWithExport exporter={handleExport} />}
      exporter={handleExport}
      pagination={<Pagination />}
      sort={{ field: 'issueDate', order: 'DESC' }}
      sx={{
        '& .MuiPaper-root': {
          boxShadow: {
            xs: 'none',
            sm: 'none',
            md: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
          }
        }
      }}
    >
      <FlexibleListLayout rowClick="show" bulkActionButtons={false}>
        <DateField source="issueDate" label={translate('resources.invoices.fields.issueDate')} />
        <DateField source="paymentDeadline" label={translate('resources.invoices.fields.paymentDeadline')} />
        <EnumTextField
          source="status"
          translationKeyPrefix="values.depositStatus"
          label={translate('resources.invoices.fields.status')}
        />
        <LinkToInvoiceButton />
        <LinkToReceiptButton />
        <LinkToInvoiceDetailButton />
      </FlexibleListLayout>
    </List>
  );
};

export const InvoiceShow = () => (
  <Show title="支払い管理 - 詳細">
    <SimpleShowLayout>
      <DateField source="issueDate" />
      <DateField source="salesStartDate" />
      <DateField source="salesEndDate" />
      <NumberField source="subTotal" />
      <NumberField source="salesTax" />
      <NumberField source="totalAmount" />
      <DateField source="paymentDeadline" />
      <EnumTextField source="status" translationKeyPrefix="values.depositStatus" />
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
        <LinkToInvoiceButton />
        <LinkToReceiptButton />
        <LinkToInvoiceDetailButton />
      </Box>
    </SimpleShowLayout>
  </Show>
);

export const InvoiceEdit = () => (
  <Edit>
    <SimpleForm sx={{ maxWidth: '600px' }}>
      <TextInput source="name" label="名前" fullWidth />
      <TextInput source="email" label="メール" fullWidth />
      <NumericRadioButtonGroupInput source="status" label="ステータス" choices={PAYMENT_STATUS} />
    </SimpleForm>
  </Edit>
);
